
import React, { useEffect, useState } from "react";
import "./Profile.css";
import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  Timestamp,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const Profile = () => {
  const [userData, setUserData] = useState({
    username: "",
    email: "",
    phone: "",
    purchasedPack: "",
    maxListing: 0,
    listingUsed: 0,
    planStatus: "inactive",
    validity: null,
    avatarUrl: "",
  });
  const [tokens, setTokens] = useState(0);
  const [chatPreviews, setChatPreviews] = useState([]);
  const [avatarLoading, setAvatarLoading] = useState(false);
const [avatarError, setAvatarError] = useState(false);

  


  const storage = getStorage();
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const listingLimitReached = userData.listingUsed >= userData.maxListing;
  const hasPlan = !!userData.purchasedPack;

  const validityDate = userData.validity
    ? userData.validity instanceof Timestamp
      ? userData.validity.toDate()
      : new Date(userData.validity)
    : null;
  const formattedDate = validityDate ? validityDate.toLocaleDateString() : "N/A";



  // Tokens listener
  useEffect(() => {
    const updateTokens = () =>
      setTokens(parseInt(localStorage.getItem("tokens") || "0", 10));
    updateTokens();
    window.addEventListener("tokensUpdated", updateTokens);
    return () => window.removeEventListener("tokensUpdated", updateTokens);
  }, []);

  // Activate a plan
  const activatePlan = async (plan) => {
    if (!auth.currentUser) return;

    const PLAN_LIMITS = {
      basic: { maxListing: 2, days: 15 },
      verified: { maxListing: 5, days: 30 },
      featured: { maxListing: 10, days: 60 },
    };

    const { maxListing, days } = PLAN_LIMITS[plan];
    const expiry = Timestamp.fromDate(new Date(Date.now() + days * 86400000));

    const userRef = doc(db, "user-data", auth.currentUser.uid);
    await updateDoc(userRef, {
      purchasedPack: plan,
      maxListing,
      listingUsed: 0,
      planStatus: "active",
      validity: expiry,
    });

    // Update local state immediately
    setUserData((prev) => ({
      ...prev,
      purchasedPack: plan,
      maxListing,
      listingUsed: 0,
      planStatus: "active",
      validity: expiry.toDate(),
    }));
    localStorage.setItem(
      "userData",
      JSON.stringify({
        ...userData,
        purchasedPack: plan,
        maxListing,
        listingUsed: 0,
        planStatus: "active",
        validity: expiry.toDate(),
      })
    );
  };

  // Auto-deactivate plan if expired
  useEffect(() => {
    const checkExpiry = async () => {
      if (!validityDate) return;
      if (validityDate < new Date()) {
        const userRef = doc(db, "user-data", auth.currentUser.uid);
        await updateDoc(userRef, {
          purchasedPack: "",
          maxListing: 0,
          listingUsed: 0,
          planStatus: "inactive",
          validity: null,
        });
        setUserData((prev) => ({
          ...prev,
          purchasedPack: "",
          maxListing: 0,
          listingUsed: 0,
          planStatus: "inactive",
          validity: null,
        }));
        localStorage.removeItem("userData");
      }
    };
    checkExpiry();
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [userData.validity]);

  // Time left for plan
  const getTimeLeft = () => {
    if (!validityDate) return "No active plan";
    const diff = validityDate - new Date();
    if (diff <= 0) return "Expired";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    return `${days}d ${hours}h ${minutes}m left`;
  };

  // Load user data on auth state change
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) return;
      const cachedUserData = localStorage.getItem("userData");
      if (cachedUserData) setUserData(JSON.parse(cachedUserData));

      const docRef = doc(db, "user-data", currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const purchasedPack = (Array.isArray(data.purchasedPack)
          ? data.purchasedPack[0]
          : data.purchasedPack
        )?.toLowerCase();
        setUserData({
          username: data.username,
          email: data.email,
          phone: data.phone,
          purchasedPack,
          maxListing: data.maxListing,
          listingUsed: data.listingUsed,
          planStatus: data.planStatus,
          validity: data.validity?.toDate(),
          customId: data.customId,
          avatarUrl: data.avatarUrl,
        });
        localStorage.setItem(
          "userData",
          JSON.stringify({
            username: data.username,
            email: data.email,
            phone: data.phone,
            purchasedPack,
            maxListing: data.maxListing,
            listingUsed: data.listingUsed,
            planStatus: data.planStatus,
            validity: data.validity?.toDate(),
            customId: data.customId,
            avatarUrl: data.avatarUrl,
          })
        );
      }
    });
    return () => unsubscribe();
  }, []);

  // Chat previews
  useEffect(() => {
    if (!auth.currentUser) return;
    const userId = auth.currentUser.uid;
    const q = query(
      collection(db, "chat-metadata"),
      where("participants", "array-contains", userId),
      orderBy("timestamp", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) =>
      setChatPreviews(
        snapshot.docs
          .map((doc) => doc.data())
          .sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds)
      )
    );
    return () => unsubscribe();
  }, []);

  // Handle extra listing
  const handleBuyExtraListing = async () => {
    if (tokens < 2) return alert("You need at least 2 tokens to buy an extra listing.");
    const newTokens = tokens - 2;
    localStorage.setItem("tokens", newTokens);
    window.dispatchEvent(new Event("tokensUpdated"));
    setTokens(newTokens);

    const userRef = doc(db, "user-data", auth.currentUser.uid);
    await updateDoc(userRef, { maxListing: userData.maxListing + 1 });
    setUserData((prev) => ({ ...prev, maxListing: prev.maxListing + 1 }));
    alert("1 extra listing slot added!");
  };

  const handleListClick = () => {
    if (!hasPlan) return alert("You need an active plan to list your space.");
    if (listingLimitReached)
      return alert("You’ve reached your listing limit. Buy another plan to list more");
    navigate("/homepage/choose-listing");
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem("user");
      sessionStorage.removeItem("user");
      navigate("/getting-started");
    } catch (err) {
      console.error("Logout error:", err);
      alert("Error logging out. Please try again.");
    }
  };

  // Plan info mapping
  const planInfo = {
    basic: { title: "🏠 Current Plan – Basic", photos: 4, badge: null, validityDays: 15 },
    verified: { title: "✅ Current Plan – Verified", photos: 6, badge: "✔ Verified", validityDays: 30 },
    featured: { title: "🏆 Current Plan – Premium", photos: 8, badge: "✔ Verified + Featured", validityDays: 60 },
  };
  
const [unreadCount, setUnreadCount] = useState(0);

// Real-time unread count listener
useEffect(() => {
  if (!auth.currentUser) return;
  const userId = auth.currentUser.uid;

  const inboxRef = collection(db, "ownerInbox", userId, "messages");
  const q = query(
    inboxRef,
    where("recipientId", "==", userId),
    where("status", "in", ["new", "sent", "delivered"]) // count all unread
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    // Only count messages not sent by current user
    const unread = snapshot.docs.filter(docSnap => docSnap.data().senderId !== userId).length;
    setUnreadCount(unread);
  });

  return () => unsubscribe();
}, [auth.currentUser, db]);


  return (
    <>
      <div className="back-button-container">
        <button className="back-button" onClick={() => navigate("/homepage")}>
          &#8592; Back
        </button>
      </div>

      <div className="profile-wrapper">
        <div className="profile-right">
          <div style={{ width: "100%", maxWidth: "900px", margin: "0 auto" }}>
            <div className="profile-card" style={{ marginBottom: "30px", marginTop: "20px" }}>
  <div
  className="avatar-container"
  style={{ position: "relative", width: "120px", height: "120px", margin: "0 auto" }}
>
  {avatarLoading ? (
    <div className="avatar-spinner"></div>
  ) : avatarError || !userData.avatarUrl ? (
    <div
      style={{
        width: "120px",
        height: "120px",
        borderRadius: "50%",
        background: "#ccc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "50px",
      }}
    >
      👤
    </div>
  ) : (
    <img
      src={userData.avatarUrl}
      alt="Profile"
      style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover" }}
      onLoad={() => setAvatarLoading(false)}
      onError={() => {
        setAvatarLoading(false);
        setAvatarError(true);
      }}
    />
  )}

  <label
    htmlFor="avatarUpload"
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      background: "#fff",
      borderRadius: "50%",
      padding: "6px",
      cursor: "pointer",
      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    }}
  >
    ✏️
  </label>

  <input
    id="avatarUpload"
    type="file"
    accept="image/*"
    style={{ display: "none" }}
    onChange={async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setAvatarLoading(true);
      setAvatarError(false); // reset error when uploading new image
      const storageRef = ref(storage, `avatars/${auth.currentUser.uid}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      const userRef = doc(db, "user-data", auth.currentUser.uid);
      await updateDoc(userRef, { avatarUrl: downloadURL });
      setUserData((prev) => ({ ...prev, avatarUrl: downloadURL }));
      setAvatarLoading(false);
    }}
  />
</div>


              {/* Display username, email, and customId */}
              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <h2>{userData.username}</h2>
                <p>Email: {userData.email}</p>
                <p>ID: {userData.customId}</p>
              </div>

              {/* Plan Card */}
        {!userData.purchasedPack ? (
  <div className="plan-card inactive">
    <h4>🚫 Current Plan – Not Active</h4>
    <p>You don’t have an active plan. Please purchase a plan to unlock full features.</p>
    <div className="buy-extra-listing">
    <button onClick={() => navigate("/homepage/buy-packs")}>Purchase Plan</button>

    </div>
  </div>
) : (
  <div className={`plan-card ${userData.purchasedPack}`}>
    {planInfo[userData.purchasedPack].badge && (
      <div className="badge">{planInfo[userData.purchasedPack].badge}</div>
    )}
    <h4>{planInfo[userData.purchasedPack].title}</h4>
    <p>🏠 Listed: {userData.listingUsed} / {userData.maxListing}</p>
    <p>🖼️ Upload up to {planInfo[userData.purchasedPack].photos} Photos</p>
    <p>💬 Chat Option Enabled</p>
    <p>Plan Valid Till: {formattedDate}</p>
    <p>⏳ Time Left: {getTimeLeft()}</p>
  </div>
)}
            </div>
            {/* Grid Section */}
            <div
              className="grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginTop: "20px",
              }}
            >
              <div
                className={`grid-item ${hasPlan && !listingLimitReached ? "" : "locked"} ${
                  location.pathname === "/homepage/choose-listing" ? "active-nav" : ""
                }`}
                onClick={handleListClick}
                style={{ cursor: hasPlan && !listingLimitReached ? "pointer" : "not-allowed", position: "relative" }}
                title={
                  !hasPlan
                    ? "Activate a plan to list your space"
                    : listingLimitReached
                    ? "Limit exceeded. Buy tokens to list more."
                    : ""
                }
              >
                <span className="material-icons">home</span>
                List Your Space
                {(!hasPlan || listingLimitReached) && <div className="lock-overlay">🔒</div>}
              </div>

              <div className="grid-item" onClick={() => navigate("/homepage/listings")}>
                <span className="material-icons">format_list_bulleted</span>
                My Listings
              </div>

               {/* Inbox */}
  <div
  className="grid-item"
  onClick={async () => {
    if (!auth.currentUser) return;
    const userId = auth.currentUser.uid;
    const inboxRef = collection(db, "ownerInbox", userId, "messages");
    const snapshot = await getDocs(inboxRef);

    // Mark all messages from others as read
    snapshot.docs.forEach(async (docSnap) => {
      const msg = docSnap.data();
      if (msg.senderId !== userId && msg.status !== "read") {
        await updateDoc(docSnap.ref, { status: "read" });
      }
    });

    navigate("/chat-preview");
  }}
  style={{ position: "relative" }}
>
  <span className="material-icons">inbox</span>
  Inbox
  {unreadCount > 0 && (
    <span
      style={{
        position: "absolute",
        top: "-5px",
        right: "-5px",
        background: "red",
        color: "white",
        borderRadius: "50%",
        padding: "4px 8px",
        fontSize: "12px",
        fontWeight: "bold",
      }}
    >
      {unreadCount}
    </span>
  )}
</div>

              <div className="grid-item" onClick={() => navigate("/saved-properties")}>
                <span className="material-icons">favorite</span>
                Saved Properties
              </div>

              <div className="grid-item" onClick={() => navigate("/report-issue")}>
                <span className="material-icons">report_problem</span>
                Report an Issue
              </div>

              <div className="grid-item wide" onClick={() => navigate("/homepage/offers")}>
                <span className="material-icons">percent</span>
                Offers and Coupons
              </div>
            </div>

            <button
              onClick={handleLogout}
              style={{
                background: "linear-gradient(90deg, #ff4d4d, #cc0000)",
                color: "white",
                padding: "12px 20px",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                width: "100%",
                marginTop: "30px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                transition: "transform 0.2s ease-in-out",
              }}
              onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
              onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
